'use client';

import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Box, Spinner, VStack } from '@chakra-ui/react';
import { AnimatePresence } from 'framer-motion';

interface Props extends PropsWithChildren {
  onLoadMore?(): Promise<void>;
  transactions: Paginated<AdaptedGiftcardTransaction | ZmyleTransactionIntent>;
}

export const GiftcardTransactionsContainer = (props: Props) => {
  const { onLoadMore, transactions, children } = props;

  const [isLoading, setIsLoading] = useState(false);

  const loadMoreElementRef = useRef<HTMLDivElement>(null);
  const intersectionObserver = useRef<IntersectionObserver>(null);

  const hasMore = Boolean(transactions?.nextPage);

  const loadMore = async () => {
    try {
      if (isLoading || !hasMore || !onLoadMore) return;

      setIsLoading(true);

      await onLoadMore();

      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!loadMoreElementRef.current) return;

    if (intersectionObserver.current) {
      intersectionObserver.current?.disconnect();

      intersectionObserver.current = null;

      if (!hasMore) return;
    }

    intersectionObserver.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMore) loadMore();
    });

    intersectionObserver.current.observe(loadMoreElementRef.current);

    return () => {
      intersectionObserver.current?.disconnect();
      intersectionObserver.current = null;
    };
  }, [intersectionObserver.current, loadMoreElementRef.current, transactions]);

  return (
    <VStack w="full" data-testid="giftcard-transactions-list">
      <AnimatePresence>{children}</AnimatePresence>

      <Spinner size="sm" visibility={isLoading ? 'visible' : 'hidden'} />

      {hasMore && <Box ref={loadMoreElementRef} />}
    </VStack>
  );
};
